import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Divider,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import '../styles/ProfessionalTable.css';
import ProfessionalTable from './ProfessionalTable';
import { API } from '../config/apiConfig';
import {
  Calculate as CalculateIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

const FeeCalculator = ({ userProfile, onFeeCalculated }) => {
  const [amount, setAmount] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [isInstant, setIsInstant] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allMethodsFees, setAllMethodsFees] = useState([]);
  const [showAllMethods, setShowAllMethods] = useState(false);

  // Load payment methods on component mount
  useEffect(() => {
    loadAllMethodsFees();
  }, []);

  const loadAllMethodsFees = async () => {
    console.log('🔍 Loading available payment methods with fee structures');
    
    try {
      setLoading(true);
      
      // Use new multi-currency endpoint - JWT-based, no userId needed
      const paymentMethodsUrl = API.availablePaymentMethods;
      console.log('📡 Calling new payment methods API:', paymentMethodsUrl);
      
      const paymentMethodsResponse = await fetch(paymentMethodsUrl, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 Payment methods response status:', paymentMethodsResponse.status);
      
      if (!paymentMethodsResponse.ok) {
        throw new Error(`HTTP error! status: ${paymentMethodsResponse.status}`);
      }
      
      const paymentMethodsData = await paymentMethodsResponse.json();
      console.log('✅ Payment methods data:', paymentMethodsData);
      
      if (paymentMethodsData.success && paymentMethodsData.data && paymentMethodsData.data.length > 0) {
        // Load payment methods with their currency and fee structure info
        const methods = paymentMethodsData.data.map(method => ({
          paymentMethodId: method.PaymentMethodId,
          paymentMethodName: method.PaymentMethodName,
          countryName: method.CountryName,
          countryCode: method.CountryCode,
          currencyCode: method.CurrencyCode || 'USD', // Default to USD if null
          currencySymbol: method.CurrencySymbol || '$',
          minimumPayout: method.MinimumPayout || 0,
          maximumPayout: method.MaximumPayout || 0,
          processingFeePercent: method.ProcessingFeePercent || 0,
          processingFeeFixed: method.ProcessingFeeFixed || 0,
          feeStructure: method.FeeStructure || 'user_pays',
          processingTimeDescription: method.ProcessingTimeDescription || '2-3 business days',
          fees: {
            userPaysFees: 0,
            userReceives: 0,
            currencyCode: method.CurrencyCode || 'USD', // Default to USD if null
            processingTimeDescription: method.ProcessingTimeDescription || '2-3 business days',
            feeStructure: method.FeeStructure || 'user_pays'
          }
        }));
        
        setAllMethodsFees(methods);
        setError(null);
        console.log('✅ Loaded payment methods:', methods.length);
        
        // Auto-select the first option
        if (methods.length > 0) {
          setPaymentMethodId(methods[0].paymentMethodId);
          console.log('✅ Auto-selected first option:', methods[0].paymentMethodName);
        }
      } else {
        console.warn('⚠️ No payment methods available for user');
        setError('No payment methods available for your country');
        setAllMethodsFees([]);
      }
    } catch (error) {
      console.error('❌ Error loading payment methods:', error);
      setError('Failed to load payment methods');
      setAllMethodsFees([]);
    } finally {
      setLoading(false);
    }
  };

  // Recalculate fees when amount changes
  useEffect(() => {
    if (amount && parseFloat(amount) > 0 && allMethodsFees.length > 0) {
      // Add a small delay to avoid too many API calls while typing
      const timeoutId = setTimeout(() => {
        recalculateFeesForAllMethods();
      }, 500); // 500ms delay
      
      return () => clearTimeout(timeoutId);
    }
  }, [amount, allMethodsFees.length]);

  const recalculateFeesForAllMethods = async () => {
    if (!amount || allMethodsFees.length === 0) return;
    
    try {
      setLoading(true);
      console.log('🔄 Recalculating fees for amount:', amount);
      
      const methodsWithFees = [];
      
      for (const method of allMethodsFees) {
        try {
          // Use new fee calculation endpoint - JWT-based
          const feeUrl = `${API.calculateFees}?amount=${amount}&paymentMethodId=${method.paymentMethodId}&isInstant=${isInstant}`;
          console.log(`📡 Calculating fees for ${method.paymentMethodName}:`, feeUrl);
          
          const feeResponse = await fetch(feeUrl, {
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (!feeResponse.ok) {
            throw new Error(`HTTP error! status: ${feeResponse.status}`);
          }
          
          const feeData = await feeResponse.json();
          console.log(`✅ Fee data for ${method.paymentMethodName}:`, feeData);
          
          if (feeData.success && feeData.data) {
            methodsWithFees.push({
              ...method,
              fees: {
                ...feeData.data,
                currencyCode: method.currencyCode || feeData.data.currencyCode,
                currencySymbol: method.currencySymbol || feeData.data.currencySymbol
              }
            });
          }
        } catch (error) {
          console.error(`❌ Error recalculating fees for ${method.paymentMethodName}:`, error);
          // Keep the method in the list with default fees
          methodsWithFees.push({
            ...method,
            fees: {
              ...method.fees,
              requestedAmount: parseFloat(amount),
              userReceives: parseFloat(amount)
            }
          });
        }
      }
      
      // Sort by user pays fees (ascending) to show best options first
      methodsWithFees.sort((a, b) => (a.fees.userPaysFees || 0) - (b.fees.userPaysFees || 0));
      
      setAllMethodsFees(methodsWithFees);
      
      // Update selected method's fees if it's still selected
      if (paymentMethodId) {
        const selectedMethod = methodsWithFees.find(m => m.paymentMethodId === parseInt(paymentMethodId));
        if (selectedMethod) {
          setFeeData(selectedMethod.fees);
          if (onFeeCalculated) {
            onFeeCalculated(selectedMethod.fees);
          }
        }
      }
      
      console.log('✅ Recalculated fees for all methods');
    } catch (error) {
      console.error('❌ Error recalculating fees:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateFees = async () => {
    if (!amount || !paymentMethodId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Use new fee calculation endpoint - JWT-based, no userId needed
      const feeUrl = `${API.calculateFees}?amount=${amount}&paymentMethodId=${paymentMethodId}&isInstant=${isInstant}`;
      console.log('📡 Calculating fees:', feeUrl);
      
      const response = await fetch(feeUrl, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Fee calculation result:', data);
      
      if (data.success && data.data) {
        setFeeData(data.data);
        if (onFeeCalculated) {
          onFeeCalculated(data.data);
        }
      } else {
        setError(data.message || 'Failed to calculate fees');
      }
    } catch (error) {
      setError('Failed to calculate fees');
      console.error('❌ Error calculating fees:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFeeStructureColor = (structure) => {
    switch (structure) {
      case 'absorbed': return 'success';
      case 'user_pays': return 'warning';
      case 'hybrid': return 'info';
      default: return 'default';
    }
  };

  const getFeeStructureLabel = (structure) => {
    switch (structure) {
      case 'absorbed': return 'No Fees (Company Absorbs)';
      case 'user_pays': return 'You Pay Fees';
      case 'hybrid': return 'Hybrid Model';
      default: return structure;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    // Handle null/undefined currency codes
    const safeCurrency = currency || 'USD';
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: safeCurrency
      }).format(amount || 0);
    } catch (error) {
      console.error('Error formatting currency:', error, 'Currency:', safeCurrency);
      // Fallback to simple format
      return `${safeCurrency} ${(amount || 0).toFixed(2)}`;
    }
  };

  return (
    <Box>
      <Card elevation={2}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CalculateIcon color="primary" />
            <Typography variant="h6">Withdrawal Fee Calculator</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Withdrawal Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Enter the amount you want to withdraw"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethodId}
                  onChange={(e) => setPaymentMethodId(e.target.value)}
                  label="Payment Method"
                  disabled={loading}
                >
                  {loading ? (
                    <MenuItem disabled>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        Loading payment methods...
                      </Box>
                    </MenuItem>
                  ) : allMethodsFees.length === 0 ? (
                    <MenuItem disabled>
                      <Typography color="text.secondary">No payment methods available</Typography>
                    </MenuItem>
                  ) : (
                    allMethodsFees.map((method) => (
                      <MenuItem key={method.paymentMethodId} value={method.paymentMethodId}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Typography component="span">{method.paymentMethodName}</Typography>
                          <Chip 
                            size="small" 
                            label={formatCurrency(method.fees.userPaysFees, method.fees.currencyCode)}
                            color={method.fees.userPaysFees === 0 ? 'success' : 'warning'}
                          />
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box mt={2} display="flex" gap={2} alignItems="center">
            <Button
              variant="contained"
              onClick={calculateFees}
              disabled={!amount || !paymentMethodId || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <CalculateIcon />}
            >
              Calculate Fees
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => setShowAllMethods(!showAllMethods)}
              startIcon={<InfoIcon />}
            >
              {showAllMethods ? 'Hide' : 'Show'} All Methods
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {feeData && (
            <Box mt={3}>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Fee Breakdown
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Withdrawal Details
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Requested Amount:</Typography>
                        <Typography fontWeight="bold">
                          {formatCurrency(feeData.requestedAmount, feeData.currencyCode)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Processing Fee:</Typography>
                        <Typography>
                          {formatCurrency(feeData.processingFee, feeData.currencyCode)}
                        </Typography>
                      </Box>
                      {feeData.instantFee > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography>Instant Fee:</Typography>
                          <Typography>
                            {formatCurrency(feeData.instantFee, feeData.currencyCode)}
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Total Fees:</Typography>
                        <Typography fontWeight="bold">
                          {formatCurrency(feeData.totalFees, feeData.currencyCode)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        What You'll Receive
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>You Pay Fees:</Typography>
                        <Typography color={feeData.userPaysFees === 0 ? 'success.main' : 'warning.main'}>
                          {formatCurrency(feeData.userPaysFees, feeData.currencyCode)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Company Absorbs:</Typography>
                        <Typography color="success.main">
                          {formatCurrency(feeData.companyAbsorbs, feeData.currencyCode)}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between" mb={2}>
                        <Typography variant="h6">You'll Receive:</Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(feeData.userReceives, feeData.currencyCode)}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip 
                          label={getFeeStructureLabel(feeData.feeStructure)}
                          color={getFeeStructureColor(feeData.feeStructure)}
                          size="small"
                        />
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        <SpeedIcon fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {feeData.processingTimeDescription}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {showAllMethods && allMethodsFees.length > 0 && (
            <Box mt={3}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                All Available Payment Methods
              </Typography>
              
              <ProfessionalTable
                columns={[
                  {
                    header: 'Payment Method',
                    accessor: 'paymentMethodName',
                    render: (value, row) => (
                      <div className="icon-cell">
                        <Typography component="span" fontWeight={500}>
                          {value}
                        </Typography>
                        {row.paymentMethodId === parseInt(paymentMethodId) && (
                          <CheckIcon color="primary" fontSize="small" />
                        )}
                      </div>
                    )
                  },
                  {
                    header: 'You Pay',
                    accessor: 'fees',
                    render: (fees) => (
                      <Typography 
                        component="span"
                        className={fees.userPaysFees === 0 ? 'amount-neutral' : 'amount-negative'}
                      >
                        {formatCurrency(fees.userPaysFees, fees.currencyCode)}
                      </Typography>
                    ),
                    style: { textAlign: 'right' }
                  },
                  {
                    header: 'You Receive',
                    accessor: 'fees',
                    render: (fees) => (
                      <Typography component="span" className="amount-positive" fontWeight={600}>
                        {formatCurrency(fees.userReceives, fees.currencyCode)}
                      </Typography>
                    ),
                    style: { textAlign: 'right' }
                  },
                  {
                    header: 'Processing Time',
                    accessor: 'fees',
                    render: (fees) => (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {fees.processingTimeDescription}
                      </Typography>
                    ),
                    style: { textAlign: 'center' }
                  },
                  {
                    header: 'Fee Structure',
                    accessor: 'fees',
                    render: (fees) => (
                      <span className={`status-badge ${getFeeStructureColor(fees.feeStructure) === 'success' ? 'status-success' : 
                        getFeeStructureColor(fees.feeStructure) === 'warning' ? 'status-warning' : 
                        getFeeStructureColor(fees.feeStructure) === 'info' ? 'status-info' : 'status-error'}`}>
                        {getFeeStructureLabel(fees.feeStructure)}
                      </span>
                    ),
                    style: { textAlign: 'center' }
                  }
                ]}
                data={allMethodsFees.map(method => ({ ...method, id: method.paymentMethodId }))}
                onRowClick={(method) => {
                  setPaymentMethodId(method.paymentMethodId);
                  setFeeData(method.fees);
                }}
                selectedRowId={parseInt(paymentMethodId)}
                loading={loading}
                emptyMessage="No payment methods available"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeeCalculator;
